/**
 * Утилиты форматирования для компонентов посадок
 */
import dayjs from 'dayjs';
import i18n, { formatDate as formatLocalizedDate, formatDateTime as formatLocalizedDateTime } from '../../../i18n';
import type { Planting } from '../../../models/Planting';

/**
 * Форматирует дату в формат YYYY-MM-DD
 */
export const formatDate = (value?: string | null): string => {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? formatLocalizedDate(value.slice(0, 10), 'short') : value;
};

/**
 * Форматирует дату и время в формат YYYY-MM-DD HH:mm
 */
export const formatDateTime = (value?: string | null): string => {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? formatLocalizedDateTime(parsed.toDate(), 'short') : value;
};

/**
 * Преобразует относительный URL в полный URL для фото
 */
export const getFullPhotoUrl = (url?: string): string => {
  if (!url) return '';
  
  // Если URL уже полный - возвращаем как есть
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // Для относительных путей добавляем хост с портом 80
  const staticHost = 'http://localhost:80';
  return url.startsWith('/') ? `${staticHost}${url}` : `${staticHost}/${url}`;
};

/**
 * Формирует краткое описание периода из локаций посадки
 */
export const formatPeriodSummary = (planting: Planting): string => {
  if (!Array.isArray(planting.locations) || planting.locations.length === 0) return '—';
  return planting.locations
    .map((location) => `${formatDate(location.fromDate)} → ${formatDate(location.toDate)}`)
    .join('; ');
};

/**
 * Формирование компактного кода локации (FieldName/P(A)/B2-5)
 */
export const getLocationCode = (planting: Planting): string => {
  const location = planting.locations?.[0];
  if (!location) return '—';
  
  const parts: string[] = [];
  
  if (location.field) {
    parts.push(location.field.name || `F${location.field.id}`);
  }
  if (location.plot) {
    parts.push(location.plot.name || `P${location.plot.id}`);
  }
  if (location.bed) {
    if (!location.bed.anchor_code) return '—';
    parts.push(`B${location.bed.anchor_code}`);
  }
  
  return parts.length > 0 ? parts.join('/') : '—';
};

/**
 * Формирует человекочитаемое название локации
 */
export const getLocationName = (planting: Planting): string => {
  const location = planting.locations?.[0];
  if (!location) return i18n.t('plantings:location.none');
  
  if (location.bed) {
    if (!location.bed.anchor_code) return i18n.t('plantings:location.none');
    return i18n.t('plantings:location.bedNamed', { name: location.bed.anchor_code });
  } else if (location.plot) {
    return i18n.t('plantings:location.plotNamed', { name: location.plot.name || location.plot.id });
  } else if (location.field) {
    return i18n.t('plantings:location.fieldNamed', { name: location.field.name || location.field.id });
  }
  
  return i18n.t('plantings:location.none');
};

export const getPlantingDisplayQuantity = (planting: Planting): number => {
  const currentQuantity = Number(planting.quantity);
  const initialQuantity = Number(planting.initialQuantity);
  if (Number.isFinite(initialQuantity) && initialQuantity > 0 && (!Number.isFinite(currentQuantity) || currentQuantity <= 0)) {
    return initialQuantity;
  }
  if (Number.isFinite(currentQuantity)) return currentQuantity;
  if (Number.isFinite(initialQuantity) && initialQuantity > 0) return initialQuantity;
  return 1;
};

/**
 * Короткий UUID (первые 8 символов)
 */
export const getShortId = (id: string | number): string => {
  const strId = String(id);
  return strId.split('-')[0] || strId.substring(0, 8);
};
